import type { OAuthContext } from '@common/api/authService';

export const BUSTLY_DESKTOP_OAUTH_CLIENT_ID = 'bustly-desktop';
const DEFAULT_BUSTLY_DESKTOP_PROTOCOL = 'bustly';
const BUSTLY_MCP_BRIDGE_ORIGINS = new Set(['https://mcp.bustly.ai']);
const LOCAL_CALLBACK_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

export type OAuthCallbackHandoffKind =
    | 'custom-protocol'
    | 'localhost-fetch'
    | 'mcp-browser-redirect'
    | 'remote-fetch';

const normalizeCustomProtocol = (value?: string | null): string | null => {
    const protocol = value?.trim().replace(/:$/, '') || '';
    if (!/^[a-z][a-z0-9+.-]*$/i.test(protocol)) {
        return null;
    }
    if (protocol === 'http' || protocol === 'https') {
        return null;
    }
    return protocol;
};

export const resolveBustlyDesktopProtocol = (
    redirectUri?: string | null,
    configuredProtocol?: string | null,
): string => {
    const target = redirectUri?.trim();
    if (target) {
        try {
            const parsed = new URL(target);
            const protocol = normalizeCustomProtocol(parsed.protocol);
            if (protocol) {
                return protocol;
            }
        } catch {
            // Fall through to the configured/default protocol.
        }
    }

    return normalizeCustomProtocol(configuredProtocol) || DEFAULT_BUSTLY_DESKTOP_PROTOCOL;
};

export const resolveBustlyDesktopOAuthContext = (params: {
    returnContext?: OAuthContext | null;
    storedContext?: OAuthContext | null;
    configuredProtocol?: string | null;
    createLoginTraceId: () => string;
}): OAuthContext => {
    const preferredContext = [params.returnContext, params.storedContext].find((context) => (
        Boolean(context?.client_id?.trim() && context?.login_trace_id?.trim())
    ));
    const protocol = resolveBustlyDesktopProtocol(
        preferredContext?.redirect_uri,
        params.configuredProtocol,
    );
    const preferredClientId = preferredContext?.client_id?.trim() || '';
    const preferredLoginTraceId = preferredContext?.login_trace_id?.trim() || '';

    if (preferredContext && preferredClientId && preferredLoginTraceId) {
        return {
            ...preferredContext,
            client_id: preferredClientId,
            redirect_uri: preferredContext.redirect_uri || `${protocol}://auth/callback`,
            device_id: preferredContext.device_id ?? null,
            login_trace_id: preferredLoginTraceId,
        };
    }

    return {
        client_id: BUSTLY_DESKTOP_OAUTH_CLIENT_ID,
        redirect_uri: `${protocol}://auth/callback`,
        fallback_redirect_uri: null,
        device_id: null,
        login_trace_id: params.createLoginTraceId(),
    };
};

export const isBustlyDesktopAuthCallbackUrl = (value: string): boolean => {
    try {
        const url = new URL(value);
        return url.protocol !== 'http:'
            && url.protocol !== 'https:'
            && url.hostname === 'auth'
            && url.pathname === '/callback'
            && Boolean(url.searchParams.get('code'))
            && Boolean(url.searchParams.get('state'));
    } catch {
        return false;
    }
};

/**
 * ChatGPT and other remote MCP clients must receive the browser's top-level
 * OAuth navigation. Unlike desktop callbacks, a background fetch cannot
 * complete their callback session.
 */
export const isBustlyMcpBridgeCallbackUrl = (value: string): boolean => {
    try {
        const url = new URL(value);
        return BUSTLY_MCP_BRIDGE_ORIGINS.has(url.origin)
            && url.pathname === '/oauth/bridge/callback'
            && Boolean(url.searchParams.get('code'))
            && Boolean(url.searchParams.get('state'));
    } catch {
        return false;
    }
};

export const resolveOAuthCallbackHandoffKind = (value: string): OAuthCallbackHandoffKind | null => {
    try {
        const url = new URL(value);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
            return 'custom-protocol';
        }
        if (url.protocol === 'http:' && LOCAL_CALLBACK_HOSTS.has(url.hostname)) {
            return 'localhost-fetch';
        }
        if (isBustlyMcpBridgeCallbackUrl(value)) {
            return 'mcp-browser-redirect';
        }
        return 'remote-fetch';
    } catch {
        return null;
    }
};
